import { QbItaParameterResult } from '@fwmovilidad/qb-home-ita-components-lib';
import { LinkOpenEnum, Patterns } from './enums';
import { DomSanitizer } from '@angular/platform-browser';
import { SecurityContext } from '@angular/core';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { BasePath } from './routePaths';
import { ResetCoupon } from '@store/flow-data/coupon';
import { ResetInitFlow } from '@store/flow-data/init-flow';
import { ResetGeneral } from '@store/flow-data/general';
import { Store } from '@ngrx/store';
import { CompanyInformation, IbanType, PersonInformation, PolicyHolder, VehicleOwner } from '@core/models/swagger';
import { SubSectionSummaryLite } from '@core/models/swagger/summaryLiteData';
import { FULLY_COMPREHENSIVE } from './constants';

dayjs.extend(customParseFormat);

export function removeEmptyFields<T>(object: T): T {
  if (object) {
    Object.keys(object).forEach(key => {
      if (object[key] && typeof object[key] === 'object') {
        this.removeEmptyFields(object[key]);
      }
      if (
        object[key] === undefined ||
        object[key] === null ||
        (Object.keys(object[key]).length === 0 && object[key].constructor === Object)
      ) {
        delete object[key];
      }
      if (object[key] instanceof Array) {
        object[key] = object[key].filter(isNotNullish);
      }
    });
  }
  return object;
}

export function isNotNullish<T>(value: T): boolean {
  return value !== undefined && value !== null;
}

export function calculateDate(date: Date, years = 0, months = 0, days = 0, hours = 0, minutes = 0, seconds = 0): Date {
  const newDate = new Date(date);

  newDate.setFullYear(newDate.getFullYear() + years);
  newDate.setMonth(newDate.getMonth() + months);
  newDate.setDate(newDate.getDate() + days);
  newDate.setHours(newDate.getHours() + hours);
  newDate.setMinutes(newDate.getMinutes() + minutes);
  newDate.setSeconds(newDate.getSeconds() + seconds);

  return newDate;
}

export function mapParameterResult(names: string[]): QbItaParameterResult[] {
  return names.map(value => {
    return {
      parameterId: value,
      parameterName: value,
    };
  });
}

export function mapFieldToParameterResult(field: string | number): QbItaParameterResult {
  const value = typeof field === 'number' ? field.toString() : field;
  return value
    ? {
        parameterId: value,
        parameterName: value,
      }
    : undefined;
}

export function findParameterResultById(
  parameters: QbItaParameterResult[],
  parameterId?: string
): QbItaParameterResult | undefined {
  return parameters?.find(parameter => parameter.parameterId === parameterId);
}

export function getLastLevelPath(path?: string): string {
  if (path === undefined) {
    return;
  }
  return path.split('/').pop();
}

/**
 * Map the response of the services to fit the components
 */
export function mapServiceResponseToComponents(response): QbItaParameterResult[] {
  return response.map(element => ({
    parameterId: element.id,
    parameterName: element.value,
  }));
}

/**
 * Redirect to a new url
 * @param url new url to go
 * @param target open in the same or a new tab
 * @param sanitizer sanitation service
 */
export function openUrl(url: string, target: LinkOpenEnum, sanitizer: DomSanitizer): void {
  const sanitizedUrl = sanitizer.sanitize(SecurityContext.RESOURCE_URL, sanitizer.bypassSecurityTrustResourceUrl(url));
  if (target === LinkOpenEnum.SAME_TAB) {
    window.location.href = sanitizedUrl;
  }
  window.open(sanitizedUrl, target);
}

export function parseDateSlashDDMMYYYY(date: string): dayjs.Dayjs {
  return dayjs(date, Patterns.DATE_SLASH_DDMMYYYY);
}

/**
 * Check if the actual route is n quick-quote
 * @returns boolean
 */
export function isQuickQuote(): boolean {
  return window.location.pathname.split('/')[1] === BasePath.QUICK_QUOTE;
}

/**
 *
 * @param date Date on string format
 * @returns Only the year
 */
export function getYearDate(date: string): string {
  return dayjs(date).get('year').toString();
}

/**
 * Return an array of coverages for the productLine
 * @param features
 * @returns Coverages[]
 */
export function getProductLineCoverages(features): [{ available: boolean; label: string; name: string }] {
  return features?.flatMap(feature => feature?.list?.filter(item => item.available === true));
}

/**
 *
 * @param genderOptions
 * @returns genderOptions without divers gender
 */
export function getGenderOptionsWithoutDivers(genderOptions: QbItaParameterResult[]): QbItaParameterResult[] {
  return genderOptions.filter(option => option.parameterId !== 'Divers');
}

export function deleteSessionStorage(store: Store): void {
  sessionStorage.clear();
  store.dispatch(ResetCoupon());
  store.dispatch(ResetInitFlow());
  store.dispatch(ResetGeneral());
}

export function isIOSSafari(): boolean {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isSafari = /Safari/i.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS/i.test(ua);
  return isIOS && isSafari;
}

/** Convert base64 to Blob, by default PDF */
export function base64ToBlob(input: string, mimeFallback = 'application/pdf'): Blob {
  let b64 = input?.trim() ?? '';
  let mime = mimeFallback;

  if (b64.startsWith('data:')) {
    const m = /^data:([^;]+);base64,(.*)$/.exec(b64);
    if (!m) {
      throw new Error('Invalid data URL');
    }
    mime = m[1] || mime;
    b64 = m[2] || '';
  }

  b64 = b64.replace(/\s+/g, '');
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) {
    bytes[i] = bin.charCodeAt(i);
  }

  return new Blob([bytes], { type: mime });
}

/**
 * Try Web Share API with files. Returns:
 * - true if the file sharing sheet opened correctly
 * - false If it is not supported or the user canceled
 */
export async function tryShareFile(file: File): Promise<boolean> {
  try {
    if (navigator?.canShare?.({ files: [file] }) && navigator?.share) {
      await navigator.share({ files: [file] });
      return true;
    }
  } catch {}
  return false;
}

export function downloadBlobViaAnchor(blob: Blob, filename: string, revokeDelayMs = 0): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();

  setTimeout(() => {
    try {
      URL.revokeObjectURL(url);
    } catch {}
  }, revokeDelayMs);
}

/**
 * 1) If it's iOS and there's Web Share -> Local Api to save files
 * 2) If not, <a download>
 */
export async function downloadPdfFromBase64(base64: string, filename: string): Promise<void> {
  const blob = base64ToBlob(base64, 'application/pdf');

  if (isIOSSafari()) {
    const file = new File([blob], filename, { type: 'application/pdf' });
    const shared = await tryShareFile(file);
    if (shared) {
      return;
    }

    downloadBlobViaAnchor(blob, filename, 60000);
    return;
  }

  downloadBlobViaAnchor(blob, filename, 0);
}

export function detectIbanType(ibanNumber: string): IbanType {
  if (!ibanNumber) {
    return;
  }
  const prefix = ibanNumber.replace(/\s+/g, '').toUpperCase().slice(0, 2);
  return prefix === 'DE' ? IbanType.GERMAN : IbanType.INTERNATIONAL;
}

export function extractPercentage(discount: string): string {
  if (!discount) return null;
  const regex = /\d+/;
  const match = regex.exec(discount);
  return match ? match[0] : null;
}

/**
 * Function to get the owner object with values
 * @param category Object with all types of owners
 * @returns Object with the owner of which we have data
 */
export function getPersonType(
  category: PolicyHolder | VehicleOwner
): PersonInformation | CompanyInformation | undefined {
  if (!category) {
    return undefined;
  }

  const person = category?.person;
  const proprietorship = (category as PolicyHolder)?.soleProprietorship;
  const company = category?.company;

  return person ? person : proprietorship ? proprietorship : company ? company : undefined;
}

/**
 * Function that tells whether a subsection is empty or not
 * @param subsection
 * @returns Boolean whether is empty or not
 */
export function hasContent(subsection: SubSectionSummaryLite): boolean {
  const ignoredKeys = ['title', 'id', 'sf_class'];

  return Object.entries(subsection).some(
    ([key, value]) => !ignoredKeys.includes(key) && value != null && value !== '' && value !== '-'
  );
}

/**
 * Compares two SF Class with the FULLY_COMPREHENSIVE list and returns the one that is greater
 * (The one with the highest index on the list)
 * @param {string} id1 First parameter to compare
 * @param {string} id2 Second parameter to compare
 * @returns {string} The parameter with the highest value
 */
export function getMayorSfClass(id1: string, id2: string): string {
  const index1 = FULLY_COMPREHENSIVE.findIndex(x => x.parameterId === id1);
  const index2 = FULLY_COMPREHENSIVE.findIndex(x => x.parameterId === id2);

  const mayorId = index1 > index2 ? id1 : id2;

  return FULLY_COMPREHENSIVE?.find(x => x.parameterId === mayorId)?.parameterName ?? '';
}
